import React, { useMemo, useRef } from 'react';
import { ModalType } from 'types';
import { useModalContext } from 'common/Context/ModalContext';

import FocusTrap from 'focus-trap-react';
import classNames from 'classnames';
import styles from './Modal.module.scss';

import AvailabilityChangeConfirm from './ModalTypes/AvailabilityChangeConfirm';
import Error from './ModalTypes/Error';
import EditConfirm from './ModalTypes/EditConfirm';
import POSDefaults from './ModalTypes/POSDefaults';
import DoorDashWarning from './ModalTypes/DoorDashWarning';
import OptionsAvailabilityUnsupportedAppWarning from './ModalTypes/OptionsAvailabilityUnsupportedAppWarning';
import BrokenImages from './ModalTypes/BrokenImages';
import DeletePhotoConfirm from './ModalTypes/DeletePhotoConfirm';
import PublishMenu from './ModalTypes/PublishMenu';
import InvalidFields from './ModalTypes/InvalidFields';
import DoshiiItemVariantAvailabilityChange from './ModalTypes/DoshiiItemVariantAvailabilityChange';
import CancelConfirm from './ModalTypes/CancelConfirm';
import BoostPeakCallToAction from './ModalTypes/BoostPeakCallToAction';
import BoostLowCallToAction from './ModalTypes/BoostLowCallToAction';
import CommonHoursConfirm from './ModalTypes/CommonHoursConfirm';

interface Props {
  activeModal?: ModalType | null;
}

const Modal: React.FC<Props> = ({ activeModal }) => {
  const { closeModal } = useModalContext();

  const canDismiss = useRef(true);

  const Component = useMemo(() => {
    const commonProps = {
      styles,
      closeModal
    };

    // reset this to default, as modal types that aren't 'ERROR' won't have this field.
    canDismiss.current = true;

    switch (activeModal?.data?.type) {
      case 'AVAILABILITY_CHANGE_CONFIRM':
        return <AvailabilityChangeConfirm {...commonProps} {...activeModal?.data} />;
      case 'ERROR':
        canDismiss.current = !!activeModal?.data?.canDismiss;
        return <Error {...commonProps} {...activeModal?.data} />;
      case 'EDIT_CONFIRM':
        return <EditConfirm {...commonProps} {...activeModal?.data} />;
      case 'INVALID_FIELDS_SINGLE_MENU_PUBLISH':
      case 'INVALID_FIELDS_SINGLE_ITEM':
        return <InvalidFields {...commonProps} {...activeModal?.data} />;
      case 'PUBLISH_MENU':
        return <PublishMenu {...commonProps} {...activeModal?.data} />;
      case 'POS_DEFAULTS':
        return <POSDefaults {...commonProps} {...activeModal?.data} />;
      case 'BROKEN_IMAGES':
        return <BrokenImages {...commonProps} />;
      case 'DOORDASH_WARNING':
        return <DoorDashWarning {...commonProps} />;
      case 'DOSHII_ITEM_VARIANT_AVAILABILITY_CHANGE':
        return <DoshiiItemVariantAvailabilityChange {...commonProps} {...activeModal?.data} />;
      case 'OPTIONS_AVAILABILITY_UNSUPPORTED_APP_WARNING':
        return <OptionsAvailabilityUnsupportedAppWarning {...commonProps} {...activeModal?.data} />;
      case 'DELETE_PHOTO':
        return <DeletePhotoConfirm {...commonProps} {...activeModal?.data} />;
      case 'CANCEL_CONFIRM':
        return <CancelConfirm {...commonProps} {...activeModal?.data} />;
      case 'BOOST_PEAK_CALL_TO_ACTION':
        return <BoostPeakCallToAction {...commonProps} {...activeModal?.data} />;
      case 'BOOST_LOW_CALL_TO_ACTION':
        return <BoostLowCallToAction {...commonProps} {...activeModal?.data} />;
      case 'COMMON_HOURS_CONFIRM':
        return <CommonHoursConfirm {...commonProps} {...activeModal?.data} />;
      default:
        return null;
    }
  }, [activeModal, closeModal]);

  return (
    <div
      className={`${styles.Outer} ${activeModal?.isVisible ? styles.Visible : ''}`}
      data-testid="modal"
      role="presentation"
    >
      {activeModal?.isVisible ? (
        <FocusTrap
          active={activeModal?.isVisible}
          focusTrapOptions={{
            escapeDeactivates: canDismiss.current,
            clickOutsideDeactivates: canDismiss.current,
            onDeactivate: closeModal
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            className={
              (activeModal?.data?.type === 'BOOST_PEAK_CALL_TO_ACTION' ||
                activeModal?.data?.type === 'BOOST_LOW_CALL_TO_ACTION') &&
              activeModal.data.isWideModal
                ? classNames(styles.Inner, styles.Wide)
                : classNames(styles.Inner)
            }
            onKeyDown={(event) => event.stopPropagation()}
            data-testid="modal-inner"
            role="presentation"
          >
            {Component}
          </div>
        </FocusTrap>
      ) : null}
    </div>
  );
};

export default Modal;
